import { useMemo, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { Link, useLocation } from "wouter";
import { ChevronDown, ChevronUp, GripVertical, Search, SlidersHorizontal, Star, X, type LucideIcon } from "lucide-react";
import { SECTIONS, SECTION_BY_ID } from "@clevis/shared-types";
import { cn } from "@clevis/shared-ui";
import { useUiPrefs } from "../hooks/useUiPrefs";

// The desktop menu, each person's own way: a search box that finds any page
// or setting as you type, Favorites pinned on top (any item or sub-item,
// dragged there in Customize mode), groups and items in the order they
// dragged them (items stay inside their group). Customize mode has arrows
// and a Pin link too, for touch screens. The groups themselves (names,
// order, what's in each) are the company's: the owner sets them under
// Roles & access → Company menu.

export interface NavEntry {
  id: string;
  href: string;
  label: string;
  icon: LucideIcon;
  /** Id of the menu group it sits in. */
  group: string;
}

/** Ask a page to open/scroll to a #section after navigating (pushState fires no hashchange). */
export function announceHash(href: string) {
  const i = href.indexOf("#");
  if (i < 0) return;
  const hash = href.slice(i + 1);
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("clevis:hash", { detail: hash }));
    document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 250);
}

function ordered<T>(all: T[], key: (t: T) => string, saved: string[] | undefined): T[] {
  if (!saved?.length) return all;
  const pos = new Map(saved.map((k, i) => [k, i]));
  return [...all].sort((a, b) => (pos.get(key(a)) ?? 1e6 + all.indexOf(a)) - (pos.get(key(b)) ?? 1e6 + all.indexOf(b)));
}
function moved<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x!);
  return next;
}

type DragKind = "group" | "item" | "sub" | "fav";

export function SidebarNav({
  items,
  groups: groupList,
  can,
  openGroups,
  onToggleGroup,
}: {
  items: NavEntry[];
  /** The company's groups, in its order. */
  groups: Array<{ id: string; label: string }>;
  can: (id: string) => boolean;
  openGroups: Record<string, boolean>;
  onToggleGroup: (group: string, open: boolean) => void;
}) {
  const [location, navigate] = useLocation();
  const { prefs, save } = useUiPrefs();
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState("");
  const drag = useRef<{ kind: DragKind; id: string; group?: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const byId = new Map(items.map((i) => [i.id, i]));
  const subs = SECTIONS.filter((s) => s.parent && byId.has(s.parent) && can(s.id));
  const groups = ordered(groupList, (g) => g.id, prefs.navGroups)
    .filter((g) => items.some((i) => i.group === g.id))
    .map((g) => g.id);
  const groupMeta = new Map(groupList.map((g, i) => [g.id, { label: g.label, firstTwo: i < 2 }]));
  const itemsIn = (g: string) => ordered(items.filter((i) => i.group === g), (i) => i.id, prefs.navItems?.[g]);
  const favorites = (prefs.favorites ?? []).filter((id) => byId.has(id) || subs.some((s) => s.id === id));
  const fav = new Set(favorites);

  const hrefOf = (id: string) => byId.get(id)?.href ?? SECTION_BY_ID.get(id)?.href ?? "/";
  const labelOf = (id: string) => byId.get(id)?.label ?? SECTION_BY_ID.get(id)?.label ?? id;
  const iconOf = (id: string) => byId.get(id)?.icon ?? byId.get(SECTION_BY_ID.get(id)?.parent ?? "")?.icon ?? Star;
  const active = (href: string) => {
    const path = href.split("#")[0]!;
    return location === path || (path !== "/" && location.startsWith(`${path}/`));
  };

  // ----- search: every page and setting, as you type -----
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const rows: Array<{ id: string; label: string; where: string; href: string; icon: LucideIcon }> = [];
    for (const g of groups) for (const i of itemsIn(g)) if (i.label.toLowerCase().includes(needle)) rows.push({ id: i.id, label: i.label, where: groupMeta.get(g)?.label ?? g, href: i.href, icon: i.icon });
    for (const s of subs) {
      const parent = byId.get(s.parent!)!;
      if (s.label.toLowerCase().includes(needle) || parent.label.toLowerCase().includes(needle)) rows.push({ id: s.id, label: s.label, where: parent.label, href: s.href, icon: parent.icon });
    }
    return rows.slice(0, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, items, prefs.navGroups, prefs.navItems]);
  function go(href: string) {
    setQ("");
    navigate(href.split("#")[0]!);
    announceHash(href);
  }
  function onSearchKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && results[0]) {
      e.preventDefault();
      go(results[0].href);
    } else if (e.key === "Escape") setQ("");
  }

  // ----- favorites -----
  function pin(id: string, at?: number) {
    if (fav.has(id)) return;
    const next = [...favorites];
    next.splice(at === undefined ? next.length : at, 0, id);
    save({ favorites: next });
  }
  function unpin(id: string) {
    save({ favorites: favorites.filter((f) => f !== id) });
  }
  function moveFav(id: string, to: number) {
    const from = favorites.indexOf(id);
    if (from < 0 || to < 0 || to >= favorites.length) return;
    save({ favorites: moved(favorites, from, to) });
  }
  function moveGroup(g: string, to: number) {
    const from = groups.indexOf(g);
    if (from < 0 || to < 0 || to >= groups.length) return;
    save({ navGroups: moved(groups, from, to) });
  }
  function moveItem(g: string, id: string, to: number) {
    const list = itemsIn(g).map((i) => i.id);
    const from = list.indexOf(id);
    if (from < 0 || to < 0 || to >= list.length) return;
    save({ navItems: { ...(prefs.navItems ?? {}), [g]: moved(list, from, to) } });
  }

  const startDrag = (kind: DragKind, id: string, group?: string) =>
    editing
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            drag.current = { kind, id, group };
            e.dataTransfer.effectAllowed = "move";
          },
          onDragEnd: () => {
            drag.current = null;
            setOver(null);
          },
        }
      : {};
  /** Groups take groups; items take items from the same group. */
  const dropHere = (kind: "group" | "item", id: string, group?: string) =>
    editing
      ? {
          onDragOver: (e: DragEvent) => {
            const d = drag.current;
            if (!d || d.kind !== kind || (kind === "item" && d.group !== group)) return;
            e.preventDefault();
            setOver(`${kind}:${id}`);
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            const d = drag.current;
            setOver(null);
            if (!d || d.kind !== kind || d.id === id) return;
            if (kind === "group") moveGroup(d.id, groups.indexOf(id));
            else if (d.group === group) moveItem(group!, d.id, itemsIn(group!).findIndex((i) => i.id === id));
          },
        }
      : {};
  /** Favorites take items, sub-items and favorites (reorder); `at` is where it lands. */
  const dropFav = (at: number | null) =>
    editing
      ? {
          onDragOver: (e: DragEvent) => {
            const d = drag.current;
            if (!d || d.kind === "group") return;
            e.preventDefault();
            setOver(`fav:${at ?? "end"}`);
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            const d = drag.current;
            setOver(null);
            if (!d || d.kind === "group") return;
            if (d.kind === "fav") {
              if (at !== null && favorites[at] !== d.id) moveFav(d.id, at);
            } else pin(d.id, at ?? undefined);
          },
        }
      : {};

  const linkClass = (on: boolean) =>
    cn(
      "flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-steel-light hover:bg-white/10 hover:text-white",
      on && "bg-white/10 text-white",
    );
  const arrows = (up: () => void, down: () => void, label: string) => (
    <span className="flex shrink-0 flex-col">
      <button className="text-steel-light/60 hover:text-white" onClick={up} aria-label={`Move ${label} up`}>
        <ChevronUp size={11} />
      </button>
      <button className="text-steel-light/60 hover:text-white" onClick={down} aria-label={`Move ${label} down`}>
        <ChevronDown size={11} />
      </button>
    </span>
  );
  const pinLink = (id: string) =>
    fav.has(id) ? null : (
      <button className="shrink-0 px-1 text-[10px] text-steel-light/50 hover:text-white" onClick={() => pin(id)} title="Pin to Favorites">
        Pin
      </button>
    );

  return (
    <>
      <div className="px-2 pb-1">
        <label className="flex items-center gap-1.5 rounded-lg bg-white/10 px-2 py-1 text-steel-light focus-within:bg-white/15">
          <Search size={13} className="shrink-0 opacity-70" />
          <input
            className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-steel-light/60 focus:outline-none"
            placeholder="Find a page or setting…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKey}
            aria-label="Find a page or setting"
          />
          {q ? (
            <button className="shrink-0 opacity-70 hover:opacity-100" onClick={() => setQ("")} aria-label="Clear search">
              <X size={12} />
            </button>
          ) : null}
        </label>
      </div>

      <nav className="flex-1 overflow-y-auto px-2">
        {q.trim() ? (
          <div className="mb-2">
            {results.length === 0 ? <p className="px-3 py-2 text-xs text-steel-light/70">Nothing called "{q.trim()}".</p> : null}
            {results.map((r, i) => (
              <button key={r.id} className={cn("flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-steel-light hover:bg-white/10 hover:text-white", i === 0 && "bg-white/5")} onClick={() => go(r.href)}>
                <r.icon size={15} className="shrink-0" />
                <span className="min-w-0 flex-1 truncate">{r.label}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-steel-light/50">{r.where}</span>
              </button>
            ))}
            {results.length ? <p className="px-3 pt-1 text-[10px] text-steel-light/50">Enter opens the first one · Esc clears</p> : null}
          </div>
        ) : null}

        {favorites.length > 0 || editing ? (
          <div className={cn("mb-1 rounded-lg", over === "fav:end" && "ring-1 ring-brandorange")} {...dropFav(null)}>
            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-brandorange/80">Favorites</div>
            {favorites.map((id, idx) => {
              const Icon = iconOf(id);
              return (
                <div key={id} className={cn("flex items-center", over === `fav:${idx}` && "rounded-lg ring-1 ring-brandorange")} {...startDrag("fav", id)} {...dropFav(idx)}>
                  {editing ? <GripVertical size={12} className="ml-1 shrink-0 cursor-grab text-steel-light/50" /> : null}
                  <Link href={hrefOf(id)} className={linkClass(!editing && active(hrefOf(id)))} onClick={() => announceHash(hrefOf(id))}>
                    <Icon size={15} />
                    <span className="truncate">{labelOf(id)}</span>
                  </Link>
                  {editing ? arrows(() => moveFav(id, idx - 1), () => moveFav(id, idx + 1), labelOf(id)) : null}
                  {editing ? (
                    <button className="shrink-0 px-1 text-steel-light/50 hover:text-white" onClick={() => unpin(id)} aria-label={`Take ${labelOf(id)} out of Favorites`} title="Take out of Favorites">
                      <X size={12} />
                    </button>
                  ) : null}
                </div>
              );
            })}
            {editing ? (
              <div className={cn("mx-2 my-1 rounded-lg border border-dashed border-steel-light/30 px-2 py-1.5 text-center text-[11px] text-steel-light/60", over === "fav:end" && "border-brandorange text-white")}>
                Drop a page or sub-item here to pin it
              </div>
            ) : null}
          </div>
        ) : null}

        {groups.map((group, gi) => {
          const list = itemsIn(group);
          const hasActive = list.some((i) => active(i.href));
          const meta = groupMeta.get(group);
          // Open unless the person folded it; the first two groups and the one with the current page start open.
          const isOpen = openGroups[group] ?? (meta?.firstTwo || hasActive);
          const open = editing || isOpen;
          return (
            <div key={group} className={cn("mb-1 rounded-lg", over === `group:${group}` && "ring-1 ring-brandorange")} {...startDrag("group", group)} {...dropHere("group", group)}>
              <div className="flex items-center">
                {editing ? <GripVertical size={12} className="ml-1 shrink-0 cursor-grab text-steel-light/50" /> : null}
                <button
                  className={cn(
                    "flex min-w-0 flex-1 items-center justify-between rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-steel-light/70 hover:text-white",
                    !open && hasActive && "text-white",
                  )}
                  onClick={() => !editing && onToggleGroup(group, !isOpen)}
                  title={!open && hasActive ? "The page you're on is in here" : undefined}
                >
                  <span className="flex items-center gap-1.5">
                    {meta?.label ?? group}
                    {!open && hasActive ? <span className="h-1.5 w-1.5 rounded-full bg-brandorange" aria-hidden="true" /> : null}
                  </span>
                  {!editing ? <ChevronDown size={12} className={cn("transition-transform", open ? "" : "-rotate-90")} /> : null}
                </button>
                {editing ? arrows(() => moveGroup(group, gi - 1), () => moveGroup(group, gi + 1), meta?.label ?? group) : null}
              </div>
              {open
                ? list.map((item, ii) => (
                    <div key={item.id}>
                      <div
                        className={cn("flex items-center", over === `item:${item.id}` && "rounded-lg ring-1 ring-brandorange")}
                        {...startDrag("item", item.id, group)}
                        {...dropHere("item", item.id, group)}
                      >
                        {editing ? <GripVertical size={12} className="ml-1 shrink-0 cursor-grab text-steel-light/50" /> : null}
                        <Link href={item.href} className={linkClass(!editing && active(item.href))}>
                          <item.icon size={15} />
                          <span className="truncate">{item.label}</span>
                        </Link>
                        {editing ? arrows(() => moveItem(group, item.id, ii - 1), () => moveItem(group, item.id, ii + 1), item.label) : null}
                        {editing ? pinLink(item.id) : null}
                      </div>
                      {editing
                        ? subs
                            .filter((s) => s.parent === item.id)
                            .map((s) => (
                              <div key={s.id} className="flex items-center pl-5" {...startDrag("sub", s.id)}>
                                <GripVertical size={11} className="shrink-0 cursor-grab text-steel-light/40" />
                                <span className="min-w-0 flex-1 truncate px-2 py-1 text-xs text-steel-light/80">↳ {s.label}</span>
                                {pinLink(s.id)}
                              </div>
                            ))
                        : null}
                    </div>
                  ))
                : null}
            </div>
          );
        })}
      </nav>
      <div className="px-3 pb-2">
        {editing ? (
          <div className="space-y-1 rounded-lg bg-white/5 p-2 text-[11px] text-steel-light">
            <p>Drag groups and items to reorder (or use the arrows). Drag any page or sub-item up into Favorites; × takes it out.</p>
            <div className="flex gap-3">
              <button className="font-semibold text-white hover:text-brandorange" onClick={() => setEditing(false)}>
                Done
              </button>
              <button className="hover:text-white" onClick={() => save({ navGroups: [], navItems: {}, favorites: [] })}>
                Reset menu
              </button>
            </div>
          </div>
        ) : (
          <button className="flex items-center gap-1.5 text-[11px] text-steel-light/70 hover:text-white" onClick={() => setEditing(true)}>
            <SlidersHorizontal size={12} /> Customize menu
          </button>
        )}
      </div>
    </>
  );
}
